import React from "react";

import {
  List,
  TextField,
  NumberField,
  Show,
  SimpleShowLayout,
  Edit,
  Create,
  SimpleForm,
  TextInput,
  DateField,
  required,
  SearchInput,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  BooleanField,
  DateInput,
  SelectField,
  AutocompleteInput,
  UrlField,
  CreateButton,
  FilterButton,
  TopToolbar,
  DatagridConfigurable,
  SelectColumnsButton,
  usePermissions,
  BooleanInput,
} from "react-admin";
import ExportAllButton from "../components/ExportAllButton";
import StringArrayField from "../components/StringArrayField";
import BooleanExistsFilter from "../components/BooleanExistsFilter";
import {
  SEVERITY_CHOICES,
  STATUS_CHOICES,
  ADJUDICATION_CATEGORY_CHOICES,
} from "../consts";
import SeverityChipField from "../components/SeverityChipField";
import DynamicDropdownFilter from "../components/DynamicDropdownFilter";
import BulkAdjudicationButton from "../components/BulkAdjudicationButton";
const trackedCveFilters = [
  <SearchInput
    source="q_name_imageRepository_codeRepository_imageTag_availableFixedReleaseVersion"
    alwaysOn
  />,
  <TextInput source="ql_name" label="CVE List" />,
  <SelectInput label="Severity" source="severity" choices={SEVERITY_CHOICES} />,
  <SelectInput label="Status" source="status" choices={STATUS_CHOICES} />,
  <ReferenceInput
    source="vulnerableRelease"
    reference="cve/releases"
    sort={{ field: "version", order: "DESC" }}
  >
    <AutocompleteInput
      optionText={(option) => `${option.name}:${option.version}`}
      style={{ width: "20em" }}
        filterToQuery={(value) => {
          const colonIndex = value.indexOf(":");
          if (colonIndex === -1) {
            return { q_name_version: value };
          }
          
          // search each part against its own field instead
          const name = value.slice(0, colonIndex);
          const version = value.slice(colonIndex + 1);
          return {
            name: { $regex: name, $options: "i" },
            version: { $regex: version, $options: "i" },
          };
      }}
    />
  </ReferenceInput>,
  <DynamicDropdownFilter
    label="Vulnerable Dependency Name"
    source="vulnerableDependencyName"
    resource="cve/vulnerable-dependencies"
  />,
  <DynamicDropdownFilter
    label="Image repository"
    source="imageRepository"
    resource="cve/image-sources"
  />,
  <DynamicDropdownFilter
    label="Image tag"
    source="imageTag"
    resource="cve/image-builds"
  />,
  <DynamicDropdownFilter
    label="Code repository"
    source="codeRepository"
    resource="cve/code-sources"
  />,
  <BooleanExistsFilter source="availableFixedImageBuildTag" />,
  <BooleanInput source="fixable" label="Fixable" />,
];

export const RowShow = () => (
  <SimpleShowLayout>
    <TextField source="description" sx={{ whiteSpace: 'pre-wrap' }} />
    <TextField source="imageRepository" />
    <TextField source="imageTag" />
    <TextField source="availableFixedImageBuildTag" />
    <TextField source="availableFixedReleaseVersion" />
    <NumberField source="companyScore" />
    <NumberField source="cvssScore" />
    <NumberField source="epssScore" />
    <TextField source="vulnerableDependencyName" />
    <TextField source="vulnerableDependencyPackageName" />
    <TextField source="vulnerableDependencyVersion" />
    <StringArrayField source="vulnerableDependencyFixVersions" />
    <StringArrayField source="vulnerableDependencyPaths" />
    <UrlField source="githubIssue" />
    <SelectField source="adjudicationSeverity" choices={SEVERITY_CHOICES} />
    <SelectField
      source="adjudicationCategory"
      choices={ADJUDICATION_CATEGORY_CHOICES}
    />
    <TextField source="adjudicationNotes" />
    <DateField source="adjudicationSloDate" label="Adjudication SLO" />
    <DateField source="fixSloDate" label="Fix SLO" />
  </SimpleShowLayout>
);

const ListActions = () => (
  <TopToolbar>
    <SelectColumnsButton />
    <FilterButton />
    <CreateButton />
    <ExportAllButton />
  </TopToolbar>
);

export const TrackedCveList = () => (
  <List
    filters={trackedCveFilters}
    perPage={25}
    sort={{ field: "createdAt", order: "DESC" }}
    actions={<ListActions />}
  >
    <DatagridConfigurable
      bulkActionButtons={<BulkAdjudicationButton />}
      rowClick="show"
      expand={<RowShow />}
      omit={[
        "description",
        "vulnerableReleaseName",
        "fixable",
        "reachable",
        "vulnerableDependencyName",
        "vulnerableDependencyPackageName",
        "vulnerableDependencyVersion",
        "vulnerableDependencyFixVersions",
        "vulnerableDependencyPaths",
        "targetFixReleaseVersion",
        "targetFixImageTag",
        "companyScore",
        "cvssScore",
        "epssScore",
        "githubIssue",
        "adjudicationSeverity",
        "adjudicationCategory",
        "adjudicationNotes",
        "adjudicationSloDate",
        "fixSloDate",
      ]}
    >
      <TextField source="name" />
      <TextField source="description" />
      <TextField source="vulnerableReleaseName" label="Release Name" />
      <TextField source="vulnerableReleaseVersion" label="Release Version" />
      <TextField
        source="availableFixedReleaseVersion"
        label="Fixed Release Version"
      />
      <TextField source="imageRepository" label="Image" />
      <TextField source="imageTag" label="Tag" />
      <TextField source="availableFixedImageBuildTag" label="Fixed Tag" />
      <TextField
        source="targetFixReleaseVersion"
        label="Target Release Version"
      />
      <DateField source="targetFixReleaseDate" label="Target Release Date" />
      <TextField source="targetFixImageTag" label="Target Image Tag" />
      <BooleanField source="fixable" />
      <BooleanField source="reachable" />
      <TextField source="vulnerableDependencyName" />
      <TextField source="vulnerableDependencyVersion" />
      <TextField source="vulnerableDependencyFixVersions" />
      <TextField source="vulnerableDependencyPaths" />
      <TextField source="companyScore" />
      <TextField source="cvssScore" />
      <TextField source="epssScore" />
      <SeverityChipField source="severity" />
      <SelectField source="status" choices={STATUS_CHOICES} />
      <TextField source="githubIssue" />
      <SelectField source="adjudicationSeverity" choices={SEVERITY_CHOICES} />
      <SelectField
        source="adjudicationCategory"
        choices={ADJUDICATION_CATEGORY_CHOICES}
      />
      <TextField source="adjudicationNotes" />
      <DateField source="adjudicationSloDate" label="Adjudication SLO" />
      <DateField source="fixSloDate" label="Fix SLO" />
      <DateField source="createdAt" />
      <DateField source="updatedAt" />
    </DatagridConfigurable>
  </List>
);

export const TrackedCveShow = () => (
  <Show>
    <SimpleShowLayout>
      <TextField source="name" />
      <TextField source="description" sx={{ whiteSpace: 'pre-wrap' }} />
      <SelectField source="severity" choices={SEVERITY_CHOICES} />
      <SelectField source="status" choices={STATUS_CHOICES} />
      <TextField source="imageRepository" />
      <TextField source="imageTag" />
      <ReferenceField source="codeCve" reference="cve/code-cves" />
      <ReferenceField source="imageCve" reference="cve/image-cves" />
      <TextField source="codeRepository" />
      <BooleanField source="fixable" />
      <BooleanField source="reachable" />
      <NumberField source="companyScore" />
      <NumberField source="cvssScore" />
      <NumberField source="epssScore" />
      <TextField source="vulnerableDependencyName" />
      <TextField source="vulnerableDependencyPackageName" />
      <TextField source="vulnerableDependencyVersion" />
      <StringArrayField source="vulnerableDependencyFixVersions" />
      <StringArrayField source="vulnerableDependencyPaths" />
      <ReferenceField source="vulnerableRelease" reference="cve/releases" />
      <TextField source="vulnerableReleaseVersion" />
      <ReferenceField
        source="availableFixedImageBuild"
        reference="cve/image-builds"
      />
      <TextField source="availableFixedImageBuildTag" />
      <ReferenceField source="availableFixedRelease" reference="cve/releases" />
      <TextField source="availableFixedReleaseVersion" />
      <TextField source="targetFixReleaseVersion" />
      <DateField source="targetFixReleaseDate" />
      <TextField source="targetFixImageTag" />
      <UrlField source="githubIssue" />
      <SelectField source="adjudicationSeverity" choices={SEVERITY_CHOICES} />
      <SelectField
        source="adjudicationCategory"
        choices={ADJUDICATION_CATEGORY_CHOICES}
      />
      <TextField source="adjudicationNotes" />
      <DateField source="adjudicationSloDate" label="Adjudication SLO" />
      <DateField source="fixSloDate" label="Fix SLO" />
      <DateField source="createdAt" />
      <DateField source="updatedAt" />
    </SimpleShowLayout>
  </Show>
);

export const TrackedCveEdit = () => {
  const { permissions } = usePermissions();
  const isAdmin =
    permissions.includes("admin") || permissions.includes("cve_admin");

  return (
    <Edit redirect="show">
      <SimpleForm>
        <TextInput source="name" disabled />
        <TextInput source="description" disabled multiline rows={4} />
        <SelectInput
          source="severity"
          choices={SEVERITY_CHOICES}
          disabled={!isAdmin}
        />
        <TextInput source="githubIssue" disabled={!isAdmin} />
        <SelectInput
          source="status"
          choices={STATUS_CHOICES}
          validate={[required()]}
        />
        <TextInput source="targetFixReleaseVersion" />
        <DateInput source="targetFixReleaseDate" />
        <TextInput source="targetFixImageTag" />
        <SelectInput source="adjudicationSeverity" choices={SEVERITY_CHOICES} />
        <SelectInput
          source="adjudicationCategory"
          choices={ADJUDICATION_CATEGORY_CHOICES}
        />
        <TextInput source="adjudicationNotes" multiline rows={4} />
      </SimpleForm>
    </Edit>
  );
};

export const TrackedCveCreate = () => {
  return (
    <Create redirect="show">
      <SimpleForm>
        <TextInput source="name" validate={[required()]} />
        <TextInput
          source="description"
          validate={[required()]}
          multiline
          rows={4}
        />
        <ReferenceInput source="codeCve" reference="cve/code-cves">
          <AutocompleteInput
            optionText={(option: { name: string; codeRepository: string }) =>
              `${option.codeRepository}:${option.name}`
            }
          />
        </ReferenceInput>
        <ReferenceInput source="imageCve" reference="cve/image-cves">
          <AutocompleteInput
            optionText={(option: {
              name: string;
              imageRepository: string;
              tag: string;
            }) => `${option.imageRepository}:${option.tag}:${option.name}`}
          />
        </ReferenceInput>
        <ReferenceInput source="vulnerableRelease" reference="cve/releases">
          <AutocompleteInput
            optionText={(option: { name: string; version: string }) =>
              `${option.name}:${option.version}`
            }
          />
        </ReferenceInput>
        <ReferenceInput
          source="availableFixedImageBuild"
          reference="cve/image-builds"
        />
        <ReferenceInput source="availableFixedRelease" reference="cve/releases">
          <AutocompleteInput
            optionText={(option: { name: string; version: string }) =>
              `${option.name}:${option.version}`
            }
          />
        </ReferenceInput>
        <SelectInput
          source="status"
          choices={STATUS_CHOICES}
          validate={[required()]}
        />
        <TextInput source="targetFixReleaseVersion" />
        <DateInput source="targetFixReleaseDate" />
        <TextInput source="targetFixImageTag" />
        <TextInput source="githubIssue" />
        <SelectInput source="adjudicationSeverity" choices={SEVERITY_CHOICES} />
        <SelectInput
          source="adjudicationCategory"
          choices={ADJUDICATION_CATEGORY_CHOICES}
        />
        <TextInput source="adjudicationNotes" multiline rows={4} />
      </SimpleForm>
    </Create>
  );
};
